import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./index.module.css";
import {
  FaFolder,
  FaFileAlt,
  FaGripVertical,
  FaPlus,
  FaMinus,
  FaTrash,
} from "react-icons/fa";
import { FaArrowTurnDown } from "react-icons/fa6";

import {
  SortableTree,
  FolderTreeItemWrapper,
} from "dnd-kit-sortable-tree";

import { structuredClone } from "globalthis/implementation";
import { useDispatch, useSelector } from "react-redux";
import {
  getListGrants,
  addNewTree,
  addScholarshipTree,
  getScholarshipTree,
  removeScholarshipTree,
} from "../../../actions/grants";

// === Константы ===
// const year__ = 40;

// === Утилиты ===
const walk = (nodes, fn, parent = null) =>
  nodes.map((n, idx, arr) => {
    const clone = { ...n };
    fn(clone, parent, idx, arr);
    if (clone.children?.length) {
      clone.children = walk(clone.children, fn, clone);
    }
    return clone;
  });

const reindex = (nodes) =>
  walk(nodes, (node, parent, idx) => {
    node.ordering = idx + 1;
    if (node.children?.length) {
      node.children.forEach((c, i) => {
        c.link_ordering = i + 1;
      });
    }
  });

const normalizeTree = (nodes = [], path = []) =>
  nodes.map((node, idx) => {
    const newNode = { ...node };

    const rawChildren = Array.isArray(newNode.children)
      ? newNode.children
      : Array.isArray(newNode.child)
      ? newNode.child
      : [];

    const selfPath = [...path, idx];
    if (newNode.id === undefined || newNode.id === null || newNode.id === "") {
      newNode.id = `node-${selfPath.join(".")}`;
    }
    newNode.id = String(newNode.id);

    // link_weight -> number (0 по умолчанию)
    const toNumber = (v) =>
      v === "" || v === null || v === undefined ? 0 : Number(v) || 0;
    newNode.link_weight = toNumber(newNode.link_weight);

    newNode.children = normalizeTree(rawChildren, [...selfPath, "children"]);
    if (!Array.isArray(newNode.children)) newNode.children = [];
    newNode.collapsed = newNode.collapsed ?? false;

    delete newNode.child;
    return newNode;
  });

const findPath = (nodes, id, path = []) => {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (String(n.id) === String(id)) return [...path, i];
    if (n.children?.length) {
      const sub = findPath(n.children, id, [...path, i, "children"]);
      if (sub) return sub;
    }
  }
  return null;
};

const getByPath = (root, path) => path.reduce((acc, key) => acc?.[key], root);

const getNodeLevel = (path) => {
  if (!Array.isArray(path)) return 1;
  // фильтруем "children", потому что путь вида [0, "children", 2, "children", 1]
  const depth = path.filter((p) => p !== "children").length;
  return depth; // уровень начинается с 1
};


// 🔒 Серверный финальный лист (для подсветки) — только link_weight > 0 и нет детей
const isFinalNode = (node) => {
  const w = typeof node?.link_weight === "number" ? node.link_weight : 0;
  const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
  return w > 0 && !hasChildren;
};

// 🏆 Это «достижение» (что нужно удалять через PUT):
// - либо серверный лист (link_weight > 0, без детей)
// - либо локально добавленное достижение (xp_key != year__) ещё без детей
const isAchievementLeaf = (node, year__) => {
  const serverLeaf = isFinalNode(node);

  const localLeaf =
    !!node?.xp_key &&
    Number(node.xp_key) !== Number(year__) &&
    (!node.children || node.children.length === 0) &&
    typeof node.link_weight !== "number";

  return serverLeaf || localLeaf;
};


const findNodeById = (nodes, id) => {
  for (const n of nodes) {
    if (String(n.id) === String(id)) return n;
    if (n.children?.length) {
      const sub = findNodeById(n.children, id);
      if (sub) return sub;
    }
  }
  return null;
};

// === Компонент ===
const AchievementTree = ({ year__ }) => {
  const [items, setItems] = useState([]);
  const [isPopup, setIsPopup] = useState(false);
  const [selected, setSelected] = useState(null);
  const [popupTargetId, setPopupTargetId] = useState(null);
  const [popupMode, setPopupMode] = useState(null);

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const dispatch = useDispatch();
  const { scholarshipGrantsList = [], scholarshipTree = [] } = useSelector(
    (state) => state.grants
  );

  const prevItemsRef = useRef([]);

  useEffect(() => {
    if (Array.isArray(scholarshipTree)) {
      const normalized = normalizeTree(structuredClone(scholarshipTree));
      const indexed = reindex(normalized);
      setItems(indexed);
      prevItemsRef.current = structuredClone(indexed); // ✅ сохраняем до изменений
    }
  }, [scholarshipTree]);

  useEffect(() => {
    if (year__) {
      dispatch(getScholarshipTree(year__));
      dispatch(getListGrants(year__));
    }
  }, [year__, dispatch]);

  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  useEffect(() => {
    if (Array.isArray(scholarshipTree)) {
      const normalized = normalizeTree(structuredClone(scholarshipTree));
      setItems(reindex(normalized));
    }
  }, [scholarshipTree]);

const handleItemsChanged = async (newItems) => {
  const updated = reindex(structuredClone(newItems));
  setItems(updated);

  const changedParents = [];

  const collectChangedAchievements = (newNode, oldNode) => {
    const newChildren = newNode.children || [];
    const oldChildren = oldNode?.children || [];

    // Если хотя бы одно достижение изменилось
    const isAchievement = (node) =>
      isAchievementLeaf(node, year__) || node.link_weight > 0;

    const newAchievements = newChildren.filter(isAchievement);
    const oldAchievements = oldChildren.filter(isAchievement);

    const newIds = newAchievements.map((n) => String(n.xp_key || n.id));
    const oldIds = oldAchievements.map((n) => String(n.xp_key || n.id));

    const changed =
      newIds.length !== oldIds.length ||
      newIds.some((id, idx) => id !== oldIds[idx]);

    if (changed) {
      changedParents.push(newNode);
    }

    // рекурсивно дальше
    for (let i = 0; i < newChildren.length; i++) {
      const child = newChildren[i];
      const oldChild = oldChildren.find((o) => String(o.id) === String(child.id));
      if (child && oldChild) {
        collectChangedAchievements(child, oldChild);
      }
    }
  };

  const prev = prevItemsRef.current;

  for (let i = 0; i < updated.length; i++) {
    const newNode = updated[i];
    const oldNode = prev.find((o) => String(o.id) === String(newNode.id));
    if (oldNode) collectChangedAchievements(newNode, oldNode);
  }

  // Обновляем только изменённых родителей
  for (const parent of changedParents) {
    const children = parent.children || [];

    const personnel_indicator_ids = children
      .filter((child) => isAchievementLeaf(child, year__) || child.link_weight > 0)
      .map((child, idx) => ({
        personnel_indicator_id: Number(child.xp_key || child.id),
        ordering: idx + 1,
      }));

    await dispatch(
      addScholarshipTree(
        parent.id,
        {
          title: parent.title,
          parent_id: parent.parent_id || 0,
          level: parent.level || 2,
          ordering: parent.ordering || 1,
          xp_key: year__,
          personnel_indicator_ids,
        },
        year__
      )
    );
  }

  // Обновляем копию
  prevItemsRef.current = structuredClone(updated);
};



  const openPopup = (mode, targetId = null) => {
    setIsPopup(true);
    setPopupMode(mode);
    setPopupTargetId(targetId);
    setSelected(null);
  };

  const addItemFromPopup = async () => {
    if (!selected) return;
    const next = structuredClone(items);
    const targetPath = popupTargetId ? findPath(next, popupTargetId) : null;
    const parentNode = targetPath ? getByPath(next, targetPath) : null;

    if (popupMode === "createBranch") {
      if (parentNode && isAchievementLeaf(parentNode, year__)) {
        alert("Нельзя добавить подветку к достижению.");
        return;
      }

      const newBranch = {
        id: generateId(),
        title: selected,
        children: [],
      };

      const actionResult = await dispatch(
        addNewTree({
          title: selected,
          parent_id: parentNode?.id || 0,
          level: targetPath ? getNodeLevel(targetPath) + 1 : 1,
          ordering: parentNode?.children?.length + 1 || next.length + 1,
          xp_key: year__,
        })
      );

      if (actionResult?.meta?.requestStatus === "fulfilled") {
        dispatch(getScholarshipTree(year__));
      }
    }

    if (popupMode === "addAchievement") {
      if (!parentNode || isAchievementLeaf(parentNode, year__)) {
        alert("Нельзя добавить достижение в финальный лист или без ветки.");
        return;
      }

      const grant = scholarshipGrantsList.find((g) => g.id === +selected);
      if (!grant) return;

      const alreadyExists = parentNode.children?.some(
        (child) => Number(child.xp_key) === grant.id
      );
      if (alreadyExists) {
        alert("Это достижение уже привязано.");
        return;
      }

      const newLeaf = {
        id: generateId(),
        title: grant.title,
        xp_key: grant.id,
      };
      parentNode.children = parentNode.children ?? [];
      parentNode.children.push(newLeaf);
      
      const personnel_indicator_ids = parentNode.children
        .filter((child) => isAchievementLeaf(child, year__))
        .map((child, index) => ({
          personnel_indicator_id: Number(child.id) || Number(child.xp_key),
          ordering: index + 1,
        }));

      const result = await dispatch(
        addScholarshipTree(
          parentNode.id,
          {
            title: parentNode.title,
            parent_id: parentNode.parent_id || 0,
            level: getNodeLevel(findPath(next, parentNode.id)) || 2,
            ordering: parentNode.ordering || 1,
            xp_key: year__,
            personnel_indicator_ids,
          },
          year__
        )
      );

      if (result?.meta?.requestStatus === "fulfilled") {
        dispatch(getScholarshipTree(year__));
      }
    }

    setIsPopup(false);
    setSelected(null);
    setPopupMode(null);
    setPopupTargetId(null);
  };

  const removeItem = (itemId) => {
    const node = findNodeById(items, itemId);
    if (!node) return;
    setDeleteTarget(node);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const next = structuredClone(items);
    const path = findPath(next, deleteTarget.id);
    if (!path) return;

    const idx = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    const parentArr = getByPath(next, parentPath);
    parentArr.splice(idx, 1);

    if (isAchievementLeaf(deleteTarget, year__)) {
      const parentNode = getByPath(next, parentPath.slice(0, -1));

      const personnel_indicator_ids = (parentNode.children ?? [])
        .filter((child) => isAchievementLeaf(child, year__))
        .map((child, index) => ({
          personnel_indicator_id: Number(child.id) || Number(child.xp_key),
          ordering: index + 1,
        }));

      await dispatch(
        addScholarshipTree(
          parentNode.id,
          {
            title: parentNode.title,
            parent_id: parentNode.parent_id ?? 0,
            level: getNodeLevel(findPath(next, parentNode.id)) || 2,
            ordering: parentNode.ordering ?? 1,
            xp_key: year__,
            personnel_indicator_ids,
          },
          year__
        )
      );
    } else {
      await dispatch(removeScholarshipTree(deleteTarget.id, year__));
    }

    dispatch(getScholarshipTree(year__));
    setShowDeletePopup(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setShowDeletePopup(false);
    setDeleteTarget(null);
  };

  const stop = (e) => e.stopPropagation();

  const TreeRow = useMemo(
    () =>
      React.forwardRef((props, ref) => {
        const {
          item,
          handleProps,
          onCollapse,
          className,
          contentClassName,
          isDragging,
        } = props;

        const hasChildren = item.children?.length > 0;
        const collapsed = !!item.collapsed;
        const disableAdd = isAchievementLeaf(item, year__);

        return (
          <FolderTreeItemWrapper
            {...props}
            ref={ref}
            className={`${styles.treeItem} ${className || ""}`}
            contentClassName={`${styles.itemContent} ${contentClassName || ""}`}
            manualDrag
            showDragHandle
            disableCollapseOnItemClick
            indentationWidth={24}
          >
            <div
              className={`${styles.row} ${isFinalNode(item) ? styles.finalNode : ""} ${
                isDragging ? styles.dragging : ""
              }`}
            >
              <button
                type="button"
                className={styles.toggleButton}
                onClick={(e) => {
                  stop(e);
                  if (typeof onCollapse === 'function') {
                    onCollapse(e);
                  }
                }}
                disabled={!hasChildren}
              >
                {collapsed ? <FaPlus /> : <FaMinus />}
              </button>

              <span className={styles.nodeIcon}>
                {hasChildren ? <FaFolder /> : <FaFileAlt />}
              </span>

              <span className={styles.nodeTitle}>{item.title}</span>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={(e) => {
                    stop(e);
                    openPopup("createBranch", item.id);
                  }}
                  title="Добавить подветку"
                  disabled={disableAdd}
                >
                  <FaPlus />
                </button>

                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={(e) => {
                    stop(e);
                    openPopup("addAchievement", item.id);
                  }}
                  title="Привязать достижение"
                  disabled={disableAdd}
                >
                  <FaArrowTurnDown />
                </button>

                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  onClick={(e) => {
                    stop(e);
                    removeItem(item.id);
                  }}
                  title="Удалить"
                >
                  <FaTrash />
                </button>

                <span className={styles.dragHandle} {...handleProps}>
                  <FaGripVertical />
                </span>
              </div>
            </div>
          </FolderTreeItemWrapper>
        );
      }),
    [items, year__]
  );

  const addPopupPortal = isPopup
    ? createPortal(
        <div className={styles.popup}>
          <div className={styles.addPopup}>
            {popupMode === "createBranch" ? (
              <input
                type="text"
                value={selected || ""}
                onChange={(e) => setSelected(e.target.value)}
                placeholder="Введите название ветки"
              />
            ) : (
              <select
                value={selected || ""}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="" disabled>
                  Выберите достижение
                </option>
                {scholarshipGrantsList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            )}
            <button onClick={addItemFromPopup}>Добавить</button>
            <button onClick={() => setIsPopup(false)}>Отмена</button>
          </div>
        </div>,
        document.body
      )
    : null;

  const deletePopupPortal = showDeletePopup
    ? createPortal(
        <div className={styles.popup}>
          <div className={styles.addPopup}>
            <h3>Удаление</h3>
            <p>
              Удалить «{deleteTarget?.title}»?
              {isAchievementLeaf(deleteTarget, year__)
                ? " Это достижение: обновим родителя."
                : " Это ветка: удалим полностью."}
            </p>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button onClick={confirmDelete}>Удалить</button>
              <button onClick={cancelDelete}>Отмена</button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Дерево достижений</h2>
      </div>

      <div className={styles.empty}>
        <div className={styles.button} onClick={() => openPopup("createBranch")}>
          Добавить корневую ветку
        </div>
      </div>

      <SortableTree
        indentationWidth={24}
        items={items}
        onItemsChanged={handleItemsChanged}
        TreeItemComponent={TreeRow}
        pointerSensorOptions={{ activationConstraint: { distance: 3 } }}
        dropAnimation={{ duration: 100 }}
      />

      {addPopupPortal}
      {deletePopupPortal}
    </div>
  );
};

export default AchievementTree;
